/* eslint-disable */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";

export const PIPELINE_STEPS = [
  {
    id: "understand",
    labelKey: "Products.advertorial.pipeline_understand",
    defaultLabel: "فهم المنتج",
    durationMs: 20000,
  },
  {
    id: "persuasion",
    labelKey: "Products.advertorial.pipeline_persuasion",
    defaultLabel: "بناء استراتيجية الإقناع",
    durationMs: 20000,
  },
  {
    id: "design",
    labelKey: "Products.advertorial.pipeline_design",
    defaultLabel: "تصميم الصفحة",
    durationMs: null,
  },
  {
    id: "finish",
    labelKey: "Products.advertorial.pipeline_finish",
    defaultLabel: "إنهاء صفحة الهبوط",
    durationMs: null,
  },
];

export const FINISH_STEP_MS = 5000;

export const TOTAL_MS = PIPELINE_STEPS.reduce(
  (s, x) => s + (typeof x.durationMs === "number" ? x.durationMs : 0),
  0,
);

const AIGenerationContext = createContext(null);

export function AIProductGenerationProvider({ children }) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [fillProgress, setFillProgress] = useState(0);
  const [pipelineIndex, setPipelineIndex] = useState(-1);
  const [pipelineDone, setPipelineDone] = useState([]);
  const [generationFeature, setGenerationFeature] = useState("product_page");

  const pipelineTimerRef = useRef(null);
  const fillRafRef = useRef(null);
  const finishTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const onFinishRef = useRef(null);
  const cancelledRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (pipelineTimerRef.current) {
      clearTimeout(pipelineTimerRef.current);
      pipelineTimerRef.current = null;
    }
    if (fillRafRef.current) {
      cancelAnimationFrame(fillRafRef.current);
      fillRafRef.current = null;
    }
    if (finishTimerRef.current) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
  }, []);

  const minimize = useCallback(() => setIsMinimized(true), []);
  const expand = useCallback(() => setIsMinimized(false), []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    clearTimers();
    onFinishRef.current = null;
    setIsMinimized(false);
    setPhase("idle");
    setFillProgress(0);
    setPipelineIndex(-1);
    setPipelineDone([]);
    setGenerationFeature("product_page");
  }, [clearTimers]);

  const finishPipeline = useCallback(async () => {
    if (cancelledRef.current) return;

    setPipelineDone(PIPELINE_STEPS.map((s) => s.id));
    setFillProgress(1);
    setPipelineIndex(PIPELINE_STEPS.length);

    try {
      if (typeof onFinishRef.current === "function") {
        await onFinishRef.current();
      }
    } catch (err) {
      console.error("AI generation onFinish error:", err);
    } finally {
      if (!cancelledRef.current) {
        setPhase("done");
      }
    }
  }, []);

  const runPipelineFrom = useCallback(
    (fromIndex) => {
      if (cancelledRef.current) return;

      if (fromIndex >= PIPELINE_STEPS.length) {
        finishPipeline();
        return;
      }

      setPipelineIndex(fromIndex);
      const step = PIPELINE_STEPS[fromIndex];

      // Open-ended steps (design / finish) wait for external signals
      if (step.durationMs == null) {
        return;
      }

      pipelineTimerRef.current = setTimeout(() => {
        if (cancelledRef.current) return;
        setPipelineDone((prev) =>
          prev.includes(step.id) ? prev : [...prev, step.id],
        );
        runPipelineFrom(fromIndex + 1);
      }, step.durationMs);
    },
    [finishPipeline],
  );

  const completeBuildStep = useCallback(() => {
    if (cancelledRef.current) return;

    const designIds = PIPELINE_STEPS.slice(0, 3).map((s) => s.id);
    setPipelineDone((prev) => {
      const next = [...prev];
      designIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
    setPipelineIndex(3);
    setFillProgress(0.9);

    clearTimers();

    const finishStart = Date.now();
    const animateFinish = () => {
      if (cancelledRef.current) return;
      const t = Math.min(1, (Date.now() - finishStart) / FINISH_STEP_MS);
      setFillProgress(0.9 + t * 0.1);
      if (t < 1) {
        fillRafRef.current = requestAnimationFrame(animateFinish);
      } else {
        setPipelineDone(PIPELINE_STEPS.map((s) => s.id));
        setFillProgress(1);
        setPipelineIndex(PIPELINE_STEPS.length);
      }
    };
    fillRafRef.current = requestAnimationFrame(animateFinish);
  }, [clearTimers]);

  const completeAllStepsNow = useCallback(() => {
    clearTimers();
    setPipelineDone(PIPELINE_STEPS.map((s) => s.id));
    setFillProgress(1);
    setPipelineIndex(PIPELINE_STEPS.length);
  }, [clearTimers]);

  const startGeneration = useCallback(
    ({ onFinish, feature: featureOpt } = {}) => {
      cancelledRef.current = false;
      onFinishRef.current = onFinish || null;
      clearTimers();

      const feature =
        featureOpt === "landing_page" ? "landing_page" : "product_page";

      setGenerationFeature(feature);
      setPipelineDone([]);
      setPipelineIndex(0);
      setFillProgress(0);
      setPhase("generating");
      setIsMinimized(false);

      startTimeRef.current = Date.now();

      const DESIGN_CRAWL_MS = 120000;

      const tick = () => {
        if (cancelledRef.current) return;
        const elapsed = Date.now() - startTimeRef.current;

        let p;
        if (elapsed <= TOTAL_MS) {
          p = (elapsed / TOTAL_MS) * 0.5;
        } else {
          const designElapsed = elapsed - TOTAL_MS;
          const crawl = Math.min(1, designElapsed / DESIGN_CRAWL_MS);
          p = 0.5 + crawl * 0.4; // 0.5 → 0.9
        }

        setFillProgress((prev) => Math.max(prev, Math.min(0.9, p)));
        fillRafRef.current = requestAnimationFrame(tick);
      };
      fillRafRef.current = requestAnimationFrame(tick);

      setTimeout(() => runPipelineFrom(0), 50);
    },
    [clearTimers, runPipelineFrom],
  );

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearTimers();
    };
  }, [clearTimers]);

  return (
    <AIGenerationContext.Provider
      value={{
        isMinimized,
        phase,
        fillProgress,
        pipelineIndex,
        pipelineDone,
        minimize,
        expand,
        reset,
        generationFeature,
        startGeneration,
        setPhase,
        setFillProgress,
        setPipelineIndex,
        setPipelineDone,
        completeBuildStep,
        completeAllStepsNow,
        FINISH_STEP_MS,
      }}
    >
      {children}
    </AIGenerationContext.Provider>
  );
}

export function useAIGeneration() {
  const ctx = useContext(AIGenerationContext);
  if (!ctx) {
    throw new Error(
      "useAIGeneration must be used within AIProductGenerationProvider",
    );
  }
  return ctx;
}

export function getAlgiersMonthKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Africa/Algiers",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  return `${y}-${m}`; // "2026-10"
}

export function resolveFreeRemaining(subscription = {}, localOverride = null) {
  if (typeof localOverride === "number") {
    return Math.max(0, localOverride);
  }

  const currentMonth = getAlgiersMonthKey();
  const storedMonth = subscription.aiProductPageFreeMonth;

  if (!storedMonth || storedMonth !== currentMonth) {
    return 10;
  }

  const rem = subscription.aiProductPageFreeRemaining;
  return typeof rem === "number" ? Math.max(0, rem) : 10;
}
